import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import LockIcon from '@mui/icons-material/Lock';

import { ContactBox, Loader, LinkText } from 'ui';

import { CreateNewPortfolioModal } from 'modules/common/Modals';

import { formatDate, generateImageUrl, sanitizeHtml, allowedForPortfolio } from 'shared/functions';
import { leadAnalystParser } from 'shared/parsers';
import { orderTypes } from 'shared/constants/orderTypes';
import { ShopRelatedProductsModal } from 'modules/shop/components/ShopRelatedProductsModal';
import { artifactTypes, artifactTypesLabels } from 'shared/constants/artifactTypes';
import { usePortfolioModals } from 'shared/hooks';
import { getAnnouncementAction } from 'store/announcement/announcementActions';
import { shopProductsCategories } from 'modules/shop/shared/constants/shopProductsCategories';
import { productLineByAnnoucementType } from 'shared/mappings/annoucementTypesToProductLinesMapping';
import { fetchEntities } from 'services';
import { UpdateEntitiesModal } from 'modules/common/Modals/UpdateEntitiesModal/UpdateEntitiesModal';
import { ItemType } from 'shared/types/portfolios';

const useStyles = makeStyles(theme => ({
  card: {
    position: 'relative',
  },
  analyst: {
    marginBottom: theme.spacing(3),

    '&:not(:first-child)': {
      marginTop: theme.spacing(3),
    },
  },
  relatedArtifacts: {
    marginTop: theme.spacing(3),
  },
  restrictedArea: {
    marginTop: 0,
    marginBottom: theme.spacing(2),
  },
  teaser: {
    marginBottom: theme.spacing(2),
  },
  bold: {
    fontWeight: 500,
  },
  link: {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  restricted: {
    textAlign: 'center',
  },
  lockIcon: {
    color: theme.palette.primary.main,
    fontSize: 100,
  },
  cover: {
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    top: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(255,255,255,.8)',
    zIndex: 11,
  },
  unlockButton: {
    height: 50,
    padding: '5px 30px',
    fontSize: '1rem',
  },
  lockContainer: {
    height: '100%',
    textAlign: 'center',
    paddingTop: 40,
    paddingBottom: 40,
  },
}));

const Announcement = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { announcementId } = useParams();

  const announcement = useSelector(state => state.announcement);
  const { data, shouldRefresh } = announcement;

  const { pressContact } = data;
  const leadAnalysts = data.leadAnalysts
    ? data.leadAnalysts.map(analyst => leadAnalystParser(analyst))
    : [];
  const relatedArtifacts = data.relatedArtifacts ? data.relatedArtifacts : [];

  const getAnnouncement = () => dispatch(getAnnouncementAction({ id: announcementId }));

  const { createPortfolio, addToPortfolio } = usePortfolioModals();

  const [isRelatedProductsModalOpen, setIsRelatedProductsModalOpen] = useState(false);

  const [relatedSubscriptionData, setRelatedSubscriptionData] = useState({
    category: shopProductsCategories.creditAnalysis,
    countriesAndIndustries: [],
    productLine: productLineByAnnoucementType[data.artifactType],
  });

  const toggleRelatedProductsModal = () =>
    setIsRelatedProductsModalOpen(!isRelatedProductsModalOpen);

  useEffect(() => {
    getAnnouncement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shouldRefresh) getAnnouncement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefresh]);

  const relatedEntities = data.relatedArtifacts
    ? data.relatedArtifacts.filter(({ artifactType }) => artifactType === artifactTypes.ENTITY)
    : [];

  const entitiesCountriesAndIndustries = relatedEntities.map(({ industries, countryCode }) => ({
    country: countryCode,
    industries,
  }));

  const fetchData = async () => {
    const response = await fetchEntities({
      filter: { issuerIds: relatedEntities.map(({ artifactId }) => artifactId) },
    });

    if (response) {
      setRelatedSubscriptionData({
        category: shopProductsCategories.creditAnalysis,
        productLine: productLineByAnnoucementType[data.artifactType],
        countriesAndIndustries: entitiesCountriesAndIndustries,
      });
    }
  };

  useEffect(() => {
    setRelatedSubscriptionData({
      category: shopProductsCategories.creditAnalysis,
      productLine: productLineByAnnoucementType[data.artifactType],
      countriesAndIndustries: data.industries
        ? data.industries.map(industry => ({
            country: null,
            industries: [industry],
          }))
        : [],
    });

    if (data.relatedArtifacts && relatedEntities && relatedEntities.length) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const subheaderText =
    data.artifactType && data.publicationDate
      ? `${formatDate(data.publicationDate)} - ${artifactTypesLabels[data.artifactType]}`
      : '';

  const { publisher } = data;
  const contactPhone =
    publisher && publisher.officePhone && `${t('Phone')}: ${publisher.officePhone}`;

  const legalName = publisher && publisher.name && `${publisher.name} ${publisher.legalForm || ''}`;

  const itemsForPortfolio = {
    items: { [data.id]: data },
    itemIds: { [data.id]: data.id },
    count: 1,
    type: ItemType.SCOPEONEID,
  };

  const handleConfirm = () => {
    getAnnouncement();
    setIsRelatedProductsModalOpen(false);
  };

  return <>
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Grid container spacing={3} alignItems="center" justifyContent="flex-end">
          <Grid item>
            {allowedForPortfolio(data.artifactType) && (
              <Button variant="outlined" size="small" onClick={addToPortfolio.show}>
                {t('Add to portfolio')}
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={8}>
        <Card className={classes.card}>
          {data.restricted && (
            <Box className={classes.cover}>
              <Grid
                container
                direction="column"
                justifyContent="space-between"
                className={classes.lockContainer}
              >
                <Grid item>
                  <LockIcon className={classes.lockIcon} />
                </Grid>
                <Grid item>
                  <Button
                    color="primary"
                    variant="contained"
                    className={classes.unlockButton}
                    onClick={toggleRelatedProductsModal}
                  >
                    {t('Unlock')}
                  </Button>
                </Grid>
              </Grid>
              {isRelatedProductsModalOpen && relatedSubscriptionData && (
                <ShopRelatedProductsModal
                  data={relatedSubscriptionData}
                  open={isRelatedProductsModalOpen}
                  onClose={toggleRelatedProductsModal}
                  onConfirm={handleConfirm}
                />
              )}
            </Box>
          )}
          {(announcement.isPending || !data.id) && <Loader cover />}
          {subheaderText && (
            <CardHeader
              subheader={
                <Typography variant="body2" color="textSecondary" component="p">
                  {subheaderText}
                </Typography>
              }
            />
          )}
          {!!data.imageFilename && 
            ![artifactTypes.RESEARCH, artifactTypes.RATING_NEWS, 
              artifactTypes.MONITORING_NOTES].includes(data.artifactType) &&
            (
            <CardMedia
              component="img"
              image={generateImageUrl(data.imageFilename, data.source)}
              title={data.shortTitle}
            />
          )}
          <CardContent>
            <Typography gutterBottom variant="h5" component="h2">
              {data.title}
            </Typography>
            <Typography paragraph color="textSecondary">
              {data.shortText}
            </Typography>
          </CardContent>
          <CardContent
            className={clsx({
              [classes.restricted]: data.restricted,
            })}
          >
            {!data.restricted && (
              <Typography component="span">{sanitizeHtml(data.text)}</Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        {publisher && (
          <Card className={classes.analyst}>
            <CardContent>
              <Typography gutterBottom variant="h5" component="h2">
                {t('Contacts')}
              </Typography>
              <Typography variant="body2" color="textSecondary" component="p">
                {legalName}
              </Typography>
              <Typography variant="body2" color="textSecondary" component="p">
                {contactPhone}
              </Typography>
              {pressContact && (
                <Typography variant="body2" color="textSecondary" component="p">
                  {`${pressContact.firstName} ${pressContact.lastName}: `}
                  <a href={`mailto:${pressContact.email}`}>{pressContact.email}</a>
                </Typography>
              )}
            </CardContent>
          </Card>
        )}
        {leadAnalysts.length > 0 && <Divider />}
        {leadAnalysts.map(analyst => (
          <div key={`${analyst.firstName}-${analyst.lastName}`} className={classes.analyst}>
            <ContactBox {...analyst} showFollowRow />
          </div>
        ))}
        {relatedArtifacts.length > 0 && (
          <>
            {publisher && <Divider />}
            <Card className={clsx(publisher && classes.relatedArtifacts)}>
              <CardContent>
                <Typography gutterBottom variant="h5" component="h2">
                  {t('Related entities and instruments')}
                </Typography>
                {relatedArtifacts.map((artifact, index) => {
                  const isLast = index === relatedArtifacts.length - 1;
                  const route =
                    artifact.artifactType === orderTypes.ENTITY ? 'entities' : 'instruments';
                  return (
                    <div key={artifact.artifactId}>
                      <Box pt={2} pb={!isLast ? 2 : 0}>
                        <Typography variant="body1" color="primary" component="p">
                          <LinkText
                            to={`/${route}/${artifact.artifactId}`}
                            label={artifact.name}
                          />
                        </Typography>
                      </Box>
                      {!isLast && <Divider />}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </>
        )}
      </Grid>
    </Grid>
    <UpdateEntitiesModal
      open={addToPortfolio.isOpen}
      onClose={addToPortfolio.close}
      onClickCreateNew={createPortfolio.show}
      itemsData={itemsForPortfolio}
      itemType={data.artifactType}
    />
    <CreateNewPortfolioModal
      open={createPortfolio.isOpen}
      onClose={createPortfolio.close}
      itemsData={itemsForPortfolio}
      itemType={data.artifactType}
    />
  </>;
};

export { Announcement };
